import React, { useState, useEffect } from 'react';
import api from '../api';
import { Atom, SpinnerGap, Lightning, ShieldCheck, CurrencyCircleDollar, Compass, Clock, Warning, CheckCircle, ArrowRight, TrendDown, TrendUp, Target, Factory, ChartBar, Waves, Thermometer, Shield } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LineChart, Line } from 'recharts';

const SCENARIOS = [
  { type: 'operations', name: 'Typhoon Category 5 — Major Facility Impact', icon: Waves, params: { event: 'Category 5 typhoon makes direct landfall', affected: 'Legazpi + Eastern Visayas branches', duration: '3 weeks', wind_speed: '260 kph', storm_surge: '5 meters', estimated_damage: 'Severe structural + infrastructure' }, description: 'Simulate a direct Cat-5 hit on BSP\'s most vulnerable branches in the Bicol/Visayas corridor.' },
  { type: 'operations', name: 'Energy Price Shock +40%', icon: Lightning, params: { event: 'Electricity price increase 40%', scope: 'All BSP facilities nationwide', duration: '6 months', cause: 'LNG supply disruption + coal phase-down', diesel_backup_cost_increase: '55%' }, description: 'Model cascading cost impact across all 7 BSP sites from a sustained energy price spike.' },
  { type: 'supervisory', name: 'Agriculture Portfolio Flood Stress', icon: ShieldCheck, params: { event: 'Severe flooding across Central Luzon & Bicol', affected_sector: 'Agriculture + Fisheries', portfolio_at_risk: '35% of agri loans', crop_loss_estimate: '60% in affected regions', insurance_coverage: '28%' }, description: 'Stress-test BSFI agriculture portfolios under a severe multi-region flood event.' },
  { type: 'green_finance', name: 'Green Bond Market Downturn', icon: CurrencyCircleDollar, params: { event: 'Green bond spreads widen 200bps', market: 'Philippine sustainable bond market', duration: '18 months', trigger: 'Global risk-off + greenwashing scandal', investor_confidence_drop: '35%' }, description: 'Assess impact on BSP\'s green finance pipeline and sustainable bond issuance targets.' },
  { type: 'policy', name: 'Accelerated Carbon Pricing (₱500/ton)', icon: Compass, params: { event: '₱500/ton carbon tax introduced', timeline: 'Phased: ₱200 in 2027, ₱500 by 2028', affected: 'Energy-intensive sectors + transport + manufacturing', expected_pass_through: '60-80% to consumers' }, description: 'Model how accelerated carbon pricing propagates through BSP operations and supervised institutions.' },
  { type: 'supervisory', name: 'BSFI Systemic Climate Stress', icon: Shield, params: { event: 'Simultaneous physical + transition shock', scenario: 'La Niña flooding + coal plant closures', affected_bsfis: 'Universal banks with >30% energy/agri exposure', capital_threshold: 'CAR below 12%', timeline: '4 quarters' }, description: 'Combined physical-transition stress test across the entire supervised BSFI portfolio.' },
  { type: 'operations', name: 'El Niño Severe Drought — Water Crisis', icon: Thermometer, params: { event: 'El Niño-driven drought lasting 6 months', affected: 'NCR water supply (Head Office + SPC)', water_reduction: '40% supply cut', cooling_impact: 'HVAC efficiency drops 25%', production_impact: 'Currency production slowdown' }, description: 'Model water scarcity impact on BSP\'s largest facilities including currency production.' },
  { type: 'combined', name: 'Compound Disaster — Earthquake + Typhoon', icon: Warning, params: { event: 'M7.2 earthquake followed by typhoon within 2 weeks', affected_regions: 'Visayas + Mindanao', infrastructure_damage: 'Severe', communication_disruption: '72 hours', bank_branch_closures: '30% of rural/thrift banks' }, description: 'Worst-case compound disaster affecting multiple BSP sites and supervised institutions simultaneously.' },
  { type: 'policy', name: 'Regulatory Fast-Track — ESRM Mandate', icon: Target, params: { event: 'BSP mandates Advanced ESRM for all banks by 2027', current_compliance: '15% at Advanced maturity', required_investment: 'Estimated ₱2-5M per institution', non_compliance_penalty: 'Restrictions on new lending' }, description: 'Simulate industry readiness and systemic impact of accelerated ESRM compliance requirements.' },
  { type: 'green_finance', name: 'Taxonomy Expansion — Blue Economy', icon: Waves, params: { event: 'PH Sustainable Finance Taxonomy expanded to include Blue Economy', new_eligible_sectors: 'Fisheries, Marine tourism, Coastal infrastructure', estimated_pipeline: '₱45 bn in new eligible projects', implementation: '2027' }, description: 'Model the opportunity from taxonomy expansion and its effect on green finance volumes.' },
];

function SeverityBadge({ level }) {
  const styles = {
    low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    moderate: 'bg-amber-50 text-amber-700 border-amber-200',
    high: 'bg-orange-50 text-orange-700 border-orange-200',
    severe: 'bg-red-50 text-red-700 border-red-200',
    critical: 'bg-red-100 text-red-800 border-red-300',
  };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${styles[level] || styles.medium}`}>{level}</span>;
}

function ImpactMeter({ label, value, maxValue, color, suffix }) {
  const pct = Math.min(100, Math.abs(value) / maxValue * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]">
        <span className="text-content-secondary">{label}</span>
        <span className="font-mono font-bold" style={{ color }}>{typeof value === 'number' ? value.toLocaleString() : value}{suffix || ''}</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function CascadeStep({ stage, system, impact, severity, timeline, isLast }) {
  const sevColor = { low: '#0A4D3C', medium: '#D9943B', high: '#C2714F', critical: '#C6493A' };
  return (
    <div className="flex items-start gap-3">
      <div className="flex flex-col items-center">
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: sevColor[severity] || sevColor.medium }}>
          {stage}
        </div>
        {!isLast && <div className="w-0.5 h-8 bg-border-default mt-1" />}
      </div>
      <div className="flex-1 pb-3">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold text-content-primary">{system}</p>
          <SeverityBadge level={severity} />
        </div>
        <p className="text-[10px] text-content-secondary mt-0.5">{impact}</p>
        {timeline && <p className="text-[9px] text-content-disabled mt-0.5">Timeline: {timeline}</p>}
      </div>
    </div>
  );
}

function RecoveryChart({ data }) {
  if (!data || !data.length) return null;
  return (
    <ResponsiveContainer width="100%" height={160}>
      <BarChart data={data} barSize={32}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
        <XAxis dataKey="quarter" tick={{ fontSize: 10, fill: '#49564C' }} />
        <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#49564C' }} tickFormatter={v => `${v}%`} />
        <Tooltip formatter={v => [`${v}%`, 'Recovery']} />
        <Bar dataKey="recovery_pct" radius={[4, 4, 0, 0]}>
          {data.map((d, i) => <Cell key={i} fill={d.recovery_pct >= 80 ? '#0A4D3C' : d.recovery_pct >= 50 ? '#D9943B' : '#C2714F'} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function SimulationResults({ result, scenarioName }) {
  if (!result) return null;
  const r = result;
  const cascade = r.cascade_analysis || [];
  const recovery = r.recovery_timeline || [];
  const financial = r.financial_impact || {};
  const affected = r.affected_entities || [];
  const responses = r.recommended_responses || {};

  // Handle both old (array) and new (object) formats for recommended_responses
  const immediateActions = Array.isArray(responses) ? responses : (responses.immediate || []);
  const shortTermActions = Array.isArray(responses) ? [] : (responses.short_term || []);
  const longTermActions = Array.isArray(responses) ? [] : (responses.long_term || []);

  return (
    <div data-testid="sim-result" className="space-y-4 animate-slide-up">
      {/* Header */}
      <div className="bg-white border-2 border-brand-accent/20 rounded-xl overflow-hidden">
        <div className="px-5 py-3 bg-brand-accent/5 border-b border-brand-accent/15 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Atom size={18} className="text-brand-accent" />
            <h3 className="font-heading font-semibold text-sm text-content-primary">{scenarioName}</h3>
          </div>
          <div className="flex items-center gap-2">
            {r.confidence_level && <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${r.confidence_level === 'high' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{r.confidence_level} confidence</span>}
            {r.risk_assessment?.level && <SeverityBadge level={r.risk_assessment.level} />}
          </div>
        </div>

        {/* Executive Summary */}
        {r.executive_summary && (
          <div className="px-5 py-3 bg-brand-primary/3 border-b border-border-subtle">
            <p className="text-[10px] font-semibold text-content-disabled uppercase tracking-wider mb-1">Governor's Briefing</p>
            <p className="text-xs text-content-primary leading-relaxed">{r.executive_summary}</p>
          </div>
        )}

        {/* Baseline vs Projected Impact */}
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-surface-default rounded-lg border border-border-subtle">
              <p className="text-[10px] font-semibold text-content-disabled uppercase tracking-wider mb-2">Baseline (Current State)</p>
              <p className="text-xs text-content-primary">{r.baseline?.description || 'N/A'}</p>
              {r.baseline?.key_metrics && (
                <div className="mt-3 space-y-1.5">
                  {Object.entries(r.baseline.key_metrics).map(([k, v], i) => (
                    <div key={i} className="flex justify-between text-[10px]"><span className="text-content-secondary">{k}</span><span className="font-mono font-medium">{typeof v === 'number' ? v.toLocaleString() : v}</span></div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 bg-brand-accent/5 rounded-lg border border-brand-accent/15">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-semibold text-brand-accent uppercase tracking-wider">Projected Impact</p>
                {r.projected_impact?.severity && <SeverityBadge level={r.projected_impact.severity} />}
              </div>
              <p className="text-xs text-content-primary">{r.projected_impact?.description || 'N/A'}</p>
              {r.projected_impact?.timeline && <p className="text-[10px] text-content-disabled mt-1">Timeline: {r.projected_impact.timeline}</p>}
              {r.projected_impact?.key_metrics && (
                <div className="mt-3 space-y-1.5">
                  {Object.entries(r.projected_impact.key_metrics).map(([k, v], i) => (
                    <div key={i} className="flex justify-between text-[10px]"><span className="text-content-secondary">{k}</span><span className="font-mono font-semibold text-brand-accent">{typeof v === 'number' ? v.toLocaleString() : v}</span></div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Financial Impact Dashboard */}
      {(financial.estimated_cost_php_mn || financial.capital_impact_bps) && (
        <div className="bg-white border border-border-default rounded-xl p-5">
          <h4 className="font-heading font-semibold text-sm text-content-primary mb-4 flex items-center gap-2">
            <ChartBar size={16} className="text-brand-accent" /> Financial Impact Assessment
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {financial.estimated_cost_php_mn != null && (
              <div className="text-center p-3 bg-red-50/50 rounded-lg">
                <p className="text-lg font-heading font-bold text-red-700">₱{financial.estimated_cost_php_mn}M</p>
                <p className="text-[10px] text-content-secondary">Estimated Cost</p>
              </div>
            )}
            {financial.capital_impact_bps != null && (
              <div className="text-center p-3 bg-orange-50/50 rounded-lg">
                <p className="text-lg font-heading font-bold text-orange-700">{financial.capital_impact_bps} bps</p>
                <p className="text-[10px] text-content-secondary">Capital Impact</p>
              </div>
            )}
            {financial.revenue_impact_pct != null && (
              <div className="text-center p-3 bg-amber-50/50 rounded-lg">
                <p className="text-lg font-heading font-bold text-amber-700">{financial.revenue_impact_pct}%</p>
                <p className="text-[10px] text-content-secondary">Revenue Impact</p>
              </div>
            )}
            {financial.recovery_cost_php_mn != null && (
              <div className="text-center p-3 bg-blue-50/50 rounded-lg">
                <p className="text-lg font-heading font-bold text-blue-700">₱{financial.recovery_cost_php_mn}M</p>
                <p className="text-[10px] text-content-secondary">Recovery Cost</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cascade Analysis */}
      {cascade.length > 0 && (
        <div className="bg-white border border-border-default rounded-xl p-5">
          <h4 className="font-heading font-semibold text-sm text-content-primary mb-4 flex items-center gap-2">
            <TrendDown size={16} className="text-status-alert" /> Cascade Analysis — How the Shock Propagates
          </h4>
          <div className="ml-1">
            {cascade.map((c, i) => (
              <CascadeStep key={i} stage={c.stage} system={c.system} impact={c.impact} severity={c.severity} timeline={c.timeline} isLast={i === cascade.length - 1} />
            ))}
          </div>
        </div>
      )}

      {/* Recovery Timeline */}
      {recovery.length > 0 && (
        <div className="bg-white border border-border-default rounded-xl p-5">
          <h4 className="font-heading font-semibold text-sm text-content-primary mb-4 flex items-center gap-2">
            <TrendUp size={16} className="text-status-on-track" /> Recovery Timeline
          </h4>
          <RecoveryChart data={recovery} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            {recovery.map((r, i) => (
              <div key={i} className="p-3 bg-surface-default rounded-lg border border-border-subtle">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-mono font-bold text-brand-accent">{r.quarter}</span>
                  <span className="text-[10px] font-bold" style={{ color: r.recovery_pct >= 80 ? '#0A4D3C' : r.recovery_pct >= 50 ? '#D9943B' : '#C2714F' }}>{r.recovery_pct}%</span>
                </div>
                <p className="text-[10px] font-semibold text-content-primary">{r.phase}</p>
                <p className="text-[9px] text-content-secondary mt-0.5">{r.actions}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Affected Entities */}
      {affected.length > 0 && (
        <div className="bg-white border border-border-default rounded-xl p-5">
          <h4 className="font-heading font-semibold text-sm text-content-primary mb-3 flex items-center gap-2">
            <Factory size={16} className="text-content-secondary" /> Affected Entities
          </h4>
          <div className="space-y-2">
            {affected.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 bg-surface-default rounded-lg">
                <SeverityBadge level={a.impact_level} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-content-primary">{a.entity}</p>
                  <p className="text-[9px] text-content-disabled capitalize">{a.type}</p>
                </div>
                <p className="text-[10px] text-content-secondary max-w-[300px] text-right">{a.specific_impact}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Plan */}
      {(immediateActions.length > 0 || shortTermActions.length > 0 || longTermActions.length > 0) && (
        <div className="bg-white border border-border-default rounded-xl p-5">
          <h4 className="font-heading font-semibold text-sm text-content-primary mb-4 flex items-center gap-2">
            <Target size={16} className="text-brand-primary" /> Recommended Response Plan
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {immediateActions.length > 0 && (
              <div className="p-3 bg-red-50/30 rounded-lg border border-red-100">
                <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider mb-2">Immediate (48h)</p>
                {immediateActions.map((a, i) => (
                  <div key={i} className="flex items-start gap-1.5 mb-1.5">
                    <Warning size={10} className="text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-[10px] text-content-primary">{a}</p>
                  </div>
                ))}
              </div>
            )}
            {shortTermActions.length > 0 && (
              <div className="p-3 bg-amber-50/30 rounded-lg border border-amber-100">
                <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-2">Short-Term (1-3 mo)</p>
                {shortTermActions.map((a, i) => (
                  <div key={i} className="flex items-start gap-1.5 mb-1.5">
                    <ArrowRight size={10} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-[10px] text-content-primary">{a}</p>
                  </div>
                ))}
              </div>
            )}
            {longTermActions.length > 0 && (
              <div className="p-3 bg-emerald-50/30 rounded-lg border border-emerald-100">
                <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-2">Long-Term (6-12 mo)</p>
                {longTermActions.map((a, i) => (
                  <div key={i} className="flex items-start gap-1.5 mb-1.5">
                    <CheckCircle size={10} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <p className="text-[10px] text-content-primary">{a}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Risk Factors & Assumptions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {r.risk_assessment?.factors?.length > 0 && (
          <div className="bg-white border border-border-default rounded-xl p-4">
            <p className="text-[10px] font-semibold text-content-disabled uppercase tracking-wider mb-2">Risk Factors</p>
            {r.risk_assessment.factors.map((f, i) => (
              <div key={i} className="flex items-start gap-1.5 mb-1"><Warning size={10} className="text-status-watch mt-0.5 flex-shrink-0" /><p className="text-[10px] text-content-secondary">{f}</p></div>
            ))}
          </div>
        )}
        {r.key_assumptions?.length > 0 && (
          <div className="bg-white border border-border-default rounded-xl p-4">
            <p className="text-[10px] font-semibold text-content-disabled uppercase tracking-wider mb-2">Key Assumptions</p>
            {r.key_assumptions.map((a, i) => (
              <div key={i} className="flex items-start gap-1.5 mb-1"><CheckCircle size={10} className="text-content-disabled mt-0.5 flex-shrink-0" /><p className="text-[10px] text-content-secondary">{a}</p></div>
            ))}
          </div>
        )}
      </div>

      {r.sensitivity_notes && (
        <p className="text-[10px] text-content-disabled italic bg-surface-default rounded-lg p-3">{r.sensitivity_notes}</p>
      )}
    </div>
  );
}

export default function DigitalTwin() {
  const [simulating, setSimulating] = useState(false);
  const [activeScenario, setActiveScenario] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [customName, setCustomName] = useState('');
  const [customType, setCustomType] = useState('operations');
  const [customParams, setCustomParams] = useState('');
  const [showAllScenarios, setShowAllScenarios] = useState(false);

  useEffect(() => {
    api.get('/api/digital-twin/history').then(r => setHistory(r.data)).catch(() => {});
  }, [result]);

  const runScenario = async (scenario) => {
    setSimulating(true); setActiveScenario(scenario); setResult(null);
    try {
      const { data } = await api.post('/api/ai/digital-twin', {
        scenario_type: scenario.type,
        scenario_name: scenario.name,
        parameters: scenario.params,
      });
      setResult(data);
    } catch (e) {
      setResult({ error: 'Simulation failed', scenario_name: scenario.name, result: { baseline: {}, projected_impact: { description: 'Simulation engine unavailable. Please try again.' }, risk_assessment: { level: 'unknown' }, recommended_responses: { immediate: [], short_term: [], long_term: [] }, confidence_level: 'unknown' } });
    } finally {
      setSimulating(false);
    }
  };

  const runCustom = async () => {
    if (!customName) return;
    let params = {};
    try { params = customParams ? JSON.parse(customParams) : {}; } catch { params = { description: customParams }; }
    await runScenario({ type: customType, name: customName, params });
  };

  const displayedScenarios = showAllScenarios ? SCENARIOS : SCENARIOS.slice(0, 6);
  const typeColors = { operations: '#0A4D3C', supervisory: '#C2714F', green_finance: '#D9943B', policy: '#5B7FBF', combined: '#8B5CF6' };

  return (
    <div data-testid="digital-twin" className="space-y-5 animate-fade-in">
      <div>
        <h1 className="font-heading font-light text-3xl text-content-primary tracking-tight">Digital Twin Studio</h1>
        <p className="text-sm text-content-secondary mt-1">AI-powered scenario simulation grounded in real BSP operational data (2024–2026)</p>
      </div>

      {/* Scenario Cards */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-sm text-content-primary">Pre-Built Scenarios</h3>
          <button onClick={() => setShowAllScenarios(!showAllScenarios)} className="text-[10px] text-brand-accent font-medium hover:underline">
            {showAllScenarios ? 'Show fewer' : `Show all ${SCENARIOS.length} scenarios`}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {displayedScenarios.map((sc, i) => (
            <button key={i} data-testid={`scenario-${i}`} onClick={() => runScenario(sc)} disabled={simulating}
              className="text-left bg-white border border-border-default rounded-xl p-4 hover:-translate-y-0.5 hover:shadow-md hover:border-brand-primary/30 transition-all group disabled:opacity-50">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors" style={{ backgroundColor: `${typeColors[sc.type]}10` }}>
                  <sc.icon size={18} style={{ color: typeColors[sc.type] }} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-content-primary leading-tight">{sc.name}</p>
                  <p className="text-[9px] mt-1 px-1.5 py-0.5 rounded-full inline-block font-medium capitalize" style={{ backgroundColor: `${typeColors[sc.type]}10`, color: typeColors[sc.type] }}>{sc.type.replace('_', ' ')}</p>
                  <p className="text-[9px] text-content-disabled mt-1 line-clamp-2">{sc.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Scenario */}
      <div className="bg-white border border-border-default rounded-xl p-4">
        <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Custom Scenario Builder</h3>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-2">
            <label className="block text-[10px] font-semibold text-content-secondary uppercase mb-1">Type</label>
            <select data-testid="custom-type" value={customType} onChange={e => setCustomType(e.target.value)} className="w-full px-2.5 py-2 border border-border-default rounded-lg text-xs bg-white">
              <option value="operations">Operations</option>
              <option value="supervisory">Supervisory</option>
              <option value="green_finance">Green Finance</option>
              <option value="policy">Policy</option>
              <option value="combined">Combined</option>
            </select>
          </div>
          <div className="md:col-span-4">
            <label className="block text-[10px] font-semibold text-content-secondary uppercase mb-1">Scenario Name</label>
            <input data-testid="custom-name" type="text" value={customName} onChange={e => setCustomName(e.target.value)}
              className="w-full px-2.5 py-2 border border-border-default rounded-lg text-xs" placeholder="e.g., Sea level rise 30cm by 2050..." />
          </div>
          <div className="md:col-span-4">
            <label className="block text-[10px] font-semibold text-content-secondary uppercase mb-1">Parameters (optional JSON or text)</label>
            <input data-testid="custom-params" type="text" value={customParams} onChange={e => setCustomParams(e.target.value)}
              className="w-full px-2.5 py-2 border border-border-default rounded-lg text-xs" placeholder='e.g., {"duration": "12 months", "affected": "NCR"}' />
          </div>
          <div className="md:col-span-2 flex items-end">
            <button data-testid="run-custom" onClick={runCustom} disabled={simulating || !customName}
              className="w-full flex items-center justify-center gap-1.5 px-4 py-2 bg-brand-accent text-white rounded-lg text-xs font-semibold hover:opacity-90 disabled:opacity-50 transition-all">
              {simulating ? <SpinnerGap size={14} className="animate-spin" /> : <Atom size={14} />}
              {simulating ? 'Running...' : 'Simulate'}
            </button>
          </div>
        </div>
      </div>

      {/* Simulation Loading */}
      {simulating && (
        <div className="bg-white border-2 border-brand-accent/30 rounded-xl p-8 text-center">
          <div className="relative mx-auto w-16 h-16 mb-4">
            <SpinnerGap size={48} className="animate-spin text-brand-accent mx-auto" />
            <Atom size={20} className="text-brand-accent absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="font-heading font-semibold text-content-primary">Running Digital Twin Simulation</p>
          <p className="text-xs text-content-secondary mt-1">{activeScenario?.name}</p>
          <p className="text-[10px] text-content-disabled mt-2">Analyzing real BSP data across facilities, BSFIs, and financial metrics...</p>
          <div className="flex justify-center gap-1 mt-3">
            {[0,1,2,3,4].map(i => (
              <div key={i} className="w-2 h-2 rounded-full bg-brand-accent animate-pulse" style={{ animationDelay: `${i * 200}ms` }} />
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {result && !simulating && (
        <SimulationResults result={result.result} scenarioName={result.scenario_name} />
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-border-default rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border-default flex items-center justify-between">
            <h3 className="font-heading font-semibold text-sm text-content-primary">Simulation History</h3>
            <span className="text-[9px] text-content-disabled">{history.length} simulations</span>
          </div>
          <div className="divide-y divide-border-subtle max-h-[240px] overflow-y-auto">
            {history.slice(0, 15).map((h, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-muted/50 transition-colors cursor-pointer" onClick={() => setResult(h)}>
                <Clock size={13} className="text-content-disabled flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-content-primary truncate">{h.scenario_name}</p>
                  <p className="text-[9px] text-content-disabled capitalize">{h.scenario_type?.replace('_', ' ')} · {h.generated_at ? new Date(h.generated_at).toLocaleDateString() : ''}</p>
                </div>
                {h.result?.risk_assessment?.level && <SeverityBadge level={h.result.risk_assessment.level} />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
